'use client';

import { createLogger } from '@/lib/debug';
import { isGhostInspector } from '@/lib/ghost-inspector';
import logging from '@/lib/logging';
import {
  LinkedInConversionEvent,
  LinkedInConversionEventUser,
  LinkedInConversionEventUserIDType,
  LinkedInGTMPayload,
} from '@/types/linkedin.d';
import Cookies from 'js-cookie';
import { FC, useCallback, useEffect } from 'react';

const logger = createLogger('linkedin capi');

/** Cookie/query param key for LinkedIn First-Party Ads Tracking ID. */
const LI_FAT_ID_KEY_NAME = 'li_fat_id';

/**
 * Reads the LinkedIn First-Party Ads Tracking ID (li_fat_id) from the URL query string or cookie.
 * Used for conversion attribution when the user came from a LinkedIn ad.
 * @returns The li_fat_id value, or undefined if not present or when run on the server.
 */
function getLiFatId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const params = new URLSearchParams(window.location.search);

  const liFatId = params.get(LI_FAT_ID_KEY_NAME) || Cookies.get(LI_FAT_ID_KEY_NAME);

  if (!liFatId) return undefined;

  return liFatId;
}

/**
 * Builds a LinkedIn Conversion API user object from GTM payload userInfo.
 * Adds email as a hashed user ID when present, sets firstName/lastName, and appends li_fat_id as click ID when available.
 * @param userInfo - User data from the GTM payload (email, firstName, lastName).
 * @returns A {@link LinkedInConversionEventUser} ready for the Conversion API payload.
 */
function getConversionEventUser(userInfo: LinkedInGTMPayload['userInfo']): LinkedInConversionEventUser {
  const conversionEventUser: LinkedInConversionEventUser = {
    userIds: [],
  };

  if (userInfo?.email) {
    conversionEventUser.userIds.push({
      idType: LinkedInConversionEventUserIDType.Email,
      idValue: userInfo.email,
    });
  }

  conversionEventUser.userInfo = {
    firstName: userInfo.firstName ?? '',
    lastName: userInfo.lastName ?? '',
  };

  const clickId = getLiFatId();

  if (clickId) {
    conversionEventUser.userIds.push({
      idType: LinkedInConversionEventUserIDType.ClickID,
      idValue: clickId,
    });
  }

  return conversionEventUser;
}

/**
 * Registers a global `window.licapi` handler for LinkedIn Conversion API (CAPI) events from GTM.
 * When GTM pushes a conversion, it calls this handler; we build a CAPI payload and POST it to `/api/licapi`.
 * Skips sending events for Ghost Inspector test emails.
 * Renders nothing; only sets/cleans up the handler on mount/unmount.
 */
export const LinkedInCAPIListener: FC = () => {
  const handler = useCallback((gtmPayload: LinkedInGTMPayload) => {
    try {
      if (gtmPayload.userInfo.email && isGhostInspector(gtmPayload.userInfo.email)) return;

      const liCapiPayload: Partial<LinkedInConversionEvent> = {
        conversion: `urn:lla:llaPartnerConversion:${gtmPayload.conversionId}`,
        eventId: gtmPayload.eventId,
        conversionHappenedAt: Date.now(),
        user: getConversionEventUser(gtmPayload.userInfo),
      };

      logger.log('Triggering conversion', liCapiPayload);

      fetch('/api/licapi', {
        method: 'POST',
        body: JSON.stringify(liCapiPayload),
      });
    } catch (e) {
      logging.captureException(e);
    }
  }, []);

  useEffect(() => {
    window.licapi = handler;

    return () => {
      try {
        delete window.licapi;
      } catch (e) {
        // Maybe not set
      }
    };
  }, [handler]);

  return null;
};
